// Types
import { useTranslation } from 'react-i18next';
import { AvailableType } from '../../dish.types';

// Properties
import { ParticipantsProperties } from './participants.properties';

// Styles
import './participants.styles.scss';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { UserType } from '../../../../types/user.type';
import apiClient from '../../../../helpers/apiClient';
import { toast } from 'react-toastify';
import { appEventEmitter } from '../../../../context/EventContext';

export type ParticipantsType = {
  id: string;
  name: string;
  surname: string;
  email: string;
  smile?: boolean;
  color?: string;
  isAvailable?: boolean;
  isDeleted?: boolean;
  clientId?: string;
  clientName?: string;
  name_surname: string;
}

interface GroupedUsers {
  [clientName: string]: Array<{
    id: string;
    name: string;
    surname: string;
    name_surname: string;
    email: string;
    smile?: boolean;
    color?: string;
    isAvailable?: boolean;
    isDeleted?: boolean;
    clientId?: string;
    clientName?: string
  }>;
}

/**
 * Participants Component
 * @description Component to render participants of videocall
 * @param {ParticipantsProperties} properties
 * @returns {JSX.Element}
 */
export function ParticipantsComponent(properties: ParticipantsProperties): JSX.Element {

    // translation function
    const { t } = useTranslation();

    // Array of all users in the database for all user groups
    const [users, setUsers] = useState<ParticipantsType[]>([])

    // State to know the active clients (multiple accordions can be open)
    const [activeClients, setActiveClients] = useState<Set<string>>(new Set());

    // Flag to track if component has been initialized
    const [isInitialized, setIsInitialized] = useState<boolean>(false);

    // State to know the search term
    const [searchTerm, setSearchTerm] = useState<string>("");

    // Names for the groups
    const inCallName = t('participants.inCall');
    const noClientName = t('participants.noClient');

    /**
     * Get all users that are in the user groups
     */
    const getUsers = async () => {
      const groupsIds = properties.user.users_groups.map(user_group => user_group.group.id).join(',');

      try {
        const response = await apiClient.get('/api/user', {
          params: {
            groups: groupsIds
          }
        });

        if (!response.data.items) {
          setUsers([]);
          return;
        }

        // Filter users that are deleted or don't have access
        response.data.items = response.data.items.filter((user: UserType) => !user.is_deleted && user.access);

        // Map users to participants
        const userParticipants: ParticipantsType[] = response.data.items.map((user: UserType) => {
          return {
            id: user.id,
            name: user.name,
            surname: user.surnames,
            email: user.email,
            smile: false,
            color: 'gray',
            isAvailable: false,
            isDeleted: user.is_deleted,
            clientId: user.clientId,
            clientName: user.clientName,
            name_surname: `${user.name} ${user.surnames}`
          } as ParticipantsType;
        })
        .filter((user: ParticipantsType) => user.id !== properties.user.id);

        if (properties.participants) {
          // Add participants to the users
          const participants = Object.values(properties.participants);

          participants.forEach(participant => {
            const user = userParticipants.find(user => user.id === participant.user.id);

            if (!user) {
              userParticipants.push({
                id: participant.user.id,
                name: participant.user.name,
                surname: participant.user.surnames,
                email: participant.user.email,
                smile: true,
                color: 'orange',
                isAvailable: false,
                clientId: participant.user.clientId,
                clientName: participant.user.clientName,
                name_surname: `${participant.user.name} ${participant.user.surnames}`,
              } as ParticipantsType);
            }
          });
        }

        setUsers(userParticipants);

      } catch (error) {
        console.error('[Login]: Error getting clients from API', error);

        setUsers([]);
      }
    }

    useEffect(() => {
      getUsers();
    }, [properties.participants]);

    // call to user
    const callUser = async (available: AvailableType) => {
      if (properties.isCalling) {
        toast('Ya estás llamando a alguien');
        return;
      }

      const usersClicked = properties.clickedUsers.map(user => user.userId);

      if (usersClicked.includes(available.user.id)) return;

      properties.setIsCalling(true);

      if (available.color !== 'green') return;

      const newClickedUsers = [...properties.clickedUsers, { userId: available.user.id, socketId: available.socketId, username: `${available.user.name} ${available.user.surnames}` }];

      console.log('call:to', {
        available: available,
        room: properties.room,
      });

      await appEventEmitter.emit("call:started", {
        action: "start",
        resource: "call",
        agent: properties.user.id,
        callee: available.user.id,
        event_phase: "start",
        room: properties.room,
        participants: Object.values(properties.participants).map(participant => participant.user.id),
        user: properties.user,
      })

      properties.setClickedUser(newClickedUsers);

      properties.socket.emit('call:to', {
          available: available,
          room: properties.room,
      })
    }

    const cancelCall = async (available: AvailableType) => {
      properties.setIsCalling(false);

      // Delete user from clicked users
      properties.setClickedUser(properties.clickedUsers.filter(user => user.userId !== available.user.id));

      properties.socket.emit('call:cancel', {
        available: available,
        room: properties.room,
      });

      await appEventEmitter.emit("call:ended", {
        action: "end",
        resource: "call",
        agent: properties.user.id,
        callee: available.user.id,
        event_phase: "end",
        room: properties.room,
        user: properties.user,
      });
    }

    useEffect(() => {
      if (properties.rejectedCall) {
        properties.setClickedUser(properties.clickedUsers.filter(user => user.userId !== properties.rejectedCall));
        properties.setUserRejectedCall('');
        properties.setIsCalling(false);
      }
    }, [properties.rejectedCall]);

    useEffect(() => {
      if (properties.acceptedCall) {
        properties.setClickedUser(properties.clickedUsers.filter(user => user.userId !== properties.acceptedCall));
        properties.setUserAcceptedCall('');
        properties.setIsCalling(false);
      }
    }, [properties.acceptedCall]);

    // render user
    const renderUser = (participant: ParticipantsType) => {
      const clickedUser = properties.clickedUsers.find(user => user.userId === participant.id);

      return <Fragment key={participant.id}>
          <i
            className={`${participant.smile ? 'las la-smile' : !participant.isAvailable ? 'las la-phone disconnected' : `las la-phone ${clickedUser ? 'end' : ''}`}`}
          />
          <div className="user">
              <div className="name">{participant.name} {participant.surname}</div>
          </div>
          <span style={{ backgroundColor: participant.color || 'green', }} />
      </Fragment>
    }

  // Group users by client name
  const groupdUsersMemo = useMemo(() => {
    const groups = users.reduce<GroupedUsers>((groups, user) => {
      let isParticipant = false;

      // Check if user is a participant in the call
      Object.values(properties.participants).forEach(participant => {
        if (participant.user.id === user.id) { // If the user is a participant then add it to the inCall group
          if (!groups[inCallName]) {
            groups[inCallName] = [];
          }
          groups[inCallName].push({ // Add user to the inCall group with the corresponding properties
            ...user,
            smile: true,
            color: 'orange',
            isAvailable: false
          });
          isParticipant = true; // Set isParticipant to true
        }
      });

      if (!isParticipant) { // If the user is not a participant then add it to the client group
        const clientName = user.clientName || noClientName;
        if (!groups[clientName]) {
          groups[clientName] = [];
        }
        const available = properties.availables.find(av => av.user?.id === user.id);
        groups[clientName].push({
          ...user,
          smile: available ? available.color === 'orange' : user.isAvailable,
          color: available ? available.color : 'gray',
          isAvailable: available && available.color === 'orange' ? true : (available ? true : false)
        });
      }

      // Sort groups by name
      Object.keys(groups).sort().forEach(key => {
        const sorted = Object.keys(groups).reduce<GroupedUsers>((obj, k) => {
          obj[k] = groups[k];
          return obj;
        }, {});
        groups = sorted;
      });

      // Assert that inCall is the first group
      if (groups[inCallName]) {
        const inCall = groups[inCallName];
        delete groups[inCallName];
        groups = {
          [inCallName]: inCall,
          ...groups
        };
      }

      return groups;
    }, {});

    Object.keys(groups).forEach(clientName => {
      groups[clientName].sort((a, b) => {
        if (a.isAvailable && !b.isAvailable) {
          return -1;
        }
        if (!a.isAvailable && b.isAvailable) {
          return 1;
        }
        return 0;
      });
    });

    return groups;
  }, [users, properties.participants, properties.availables, inCallName, noClientName]);

  // Initialize activeClients only when needed
  useEffect(() => {
    const availableGroups = Object.keys(groupdUsersMemo);
    const hasInCallGroup = availableGroups.includes(inCallName);
    const inCallHasUsers = hasInCallGroup && groupdUsersMemo[inCallName].length > 0;

    setActiveClients(prev => {
      const newActiveClients = new Set(prev);

      // Always keep "En la llamada" group open if it exists and has users
      if (inCallHasUsers) {
        newActiveClients.add(inCallName);
      } else {
        // Remove "En la llamada" if it doesn't exist or doesn't have users
        newActiveClients.delete(inCallName);
      }

      // Only auto-open the first available group on initial load
      // But ONLY if there's no "En la llamada" group, or if "En la llamada" exists but we want another group open too
      if (!isInitialized && newActiveClients.size === 0 && availableGroups.length > 0) {
        newActiveClients.add(availableGroups[0]);
        setIsInitialized(true);
      } else if (!isInitialized && inCallHasUsers && availableGroups.length > 1) {
        // If "En la llamada" exists with users, also open the next available group on first load
        newActiveClients.add(availableGroups[1]);
        setIsInitialized(true);
      } else if (!isInitialized) {
        // Mark as initialized even if no groups to open
        setIsInitialized(true);
      }

      // Remove any active clients that no longer exist
      newActiveClients.forEach(clientName => {
        if (!availableGroups.includes(clientName)) {
          newActiveClients.delete(clientName);
        }
      });

      return newActiveClients;
    });
  }, [groupdUsersMemo, inCallName, isInitialized]);

  const toggleAccordion = (clientName: string) => {
    // Don't allow closing the "En la llamada" group if it exists and has users
    const inCallExists = Object.keys(groupdUsersMemo).includes(inCallName);
    const inCallHasUsers = inCallExists && groupdUsersMemo[inCallName]?.length > 0;

    console.log('toggleAccordion:', {
      clientName,
      inCallName,
      inCallExists,
      inCallHasUsers,
      currentActiveClients: Array.from(activeClients)
    });

    setActiveClients(prev => {
      const newActiveClients = new Set(prev);

      if (newActiveClients.has(clientName)) {
        // Trying to close an accordion
        if (clientName === inCallName && inCallHasUsers) {
          // Don't allow closing "En la llamada" if it has users
          console.log('Prevented closing En la llamada with users');
          return newActiveClients;
        }
        // Close the accordion
        newActiveClients.delete(clientName);
        console.log('Closed accordion:', clientName);
      } else {
        // Trying to open an accordion
        console.log('Opening accordion:', clientName);

        // Close all other accordions first
        newActiveClients.clear();

        // Always re-add "En la llamada" if it has users
        if (inCallHasUsers) {
          newActiveClients.add(inCallName);
          console.log('Re-added En la llamada');
        }

        // Add the clicked accordion
        newActiveClients.add(clientName);
        console.log('Added new accordion:', clientName);
      }

      console.log('Final active clients:', Array.from(newActiveClients));
      return newActiveClients;
    });

    setSearchTerm('');
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const normalizeString = (str: string) => {
    return str
      .replace(/[\u0300-\u036f]/g, "") // Remove accents
      .toLowerCase();
  };

  const filteredUsers = (clientName: string) => {
    return groupdUsersMemo[clientName].filter(user =>
      normalizeString(user.name_surname).includes(normalizeString(searchTerm))
    );
  };

  return (
    <div className="ParticipantsComponent Modal">
        <div className='Header'>
            {t('participants.title')}
            <i className='las la-times last' onClick={() => { properties.setScene(undefined) }}></i>
        </div>
        <div className="accordion">
            {Object.keys(groupdUsersMemo).map(clientName => (
                <div key={clientName} className={`accordion-item ${activeClients.has(clientName) ? 'active' : ''}`}>
                    <div className="accordion-header" onClick={() => toggleAccordion(clientName)}>
                        {clientName}
                        <i className={`las ${activeClients.has(clientName) ? 'la-angle-up' : 'la-angle-down'}`}></i>
                    </div>
                    <div className="accordion-content">
                       {clientName !== inCallName && (
                        <div className='Input'>
                        <i className='las la-search'></i>
                        <input
                           type="text"
                           placeholder={t('participants.search').toString()}
                           value={searchTerm}
                           onChange={handleSearch}
                         />
                        </div>
                        )}
                        <ul>
                          {filteredUsers(clientName).map(user => (
                            <li key={user.id} onClick={() => {
                              const available = properties.availables.find(av => av.user?.id === user.id);
                              if (!available || user.smile) {
                                return;
                              }

                              const clickedUser = properties.clickedUsers.find(user => user.userId === available.user.id);

                              if (clickedUser) {
                                cancelCall(available);
                                return;
                              }
                              callUser(available);
                            }}>
                              {renderUser(user)}
                            </li>
                          ))}
                          {filteredUsers(clientName).length === 0 && (
                            <li className='NoUsers'>{t('participants.noUsers')}</li>
                          )}
                        </ul>
                    </div>
                </div>
            ))}
        </div>
    </div>
  );
}
